/**
 *  Populates Menu Alignment position for menuElement (relative to hostElement) based on an algorithm
 *  This algorithm assumes menuElement is 'absolute' positioned and adjacent to hostElement. 
 *  Example: <div><hostElement /><menuElement style="position: absolute;" /></div>
    Solution: Cases in order -
      1.  Set and Test left alignment for menuElement if menu is positioned outside the left viewport
      2.  Set and Test right alignment for menuElement if menu is positioned outside the right viewport
      3.  Set center alignment for menuElement if above two cases fail
 * @param {object} Host DOM element i.e. Tab Menu, Icon Menu, Menu Button
 * @param {object} Target DOM element i.e. Menu
 * @return {'left/right': [number]px}
 */
export function getMenuAlignment(host, menu) {
  let hostElement = host;   //Clone element to avoid sliding effect?
  let menuElement = menu;   //Clone element to avoid sliding effect?
  const hostElementRect = hostElement.getBoundingClientRect();
  const menuElementRect = menuElement.getBoundingClientRect();
  let menuElementLeft = menuElementRect.left;
  let menuElementRight = menuElementRect.right;
  let windowWidth = window.innerWidth;
  let hostElementWidth = hostElementRect.width;
  let menuElementWidth = menuElementRect.width;

  if (menuElementLeft > 0 && menuElementRight <= windowWidth && !isTargetIntroducingScroll(hostElement, menuElement) && 
      isInViewport(menuElement) && !isOverlapped(menuElement)) {
    return;
  }
  else if (menuElementLeft < 0) {
    menuElement.style.left = `0px`;
    menuElement.style.right = `auto`;
    if (!isTargetIntroducingScroll(hostElement, menuElement) && isInViewport(menuElement) && !isOverlapped(menuElement)) {
      return { 'left' : '0px' };
    }
  }
  else if (menuElementRight >= windowWidth) {
    menuElement.style.left = `auto`;
    menuElement.style.right = `0px`;
    if (!isTargetIntroducingScroll(hostElement, menuElement) && isInViewport(menuElement) && !isOverlapped(menuElement)) {
      return { 'right' : '0px'};
    }
  }

  let leftPosition = (menuElementWidth - hostElementWidth) / 2;
  menuElement.style.left = `${leftPosition}px`;
  menuElement.style.right = `auto`;
  return { 'left' : `${leftPosition < 0 ? leftPosition : leftPosition * -1}px` };
}

/**
 * Populates if a DOM element is in Viewport
 * @param {object} DOM element
 * @param {boolean} should bottom part of the DOM element be skipped during the test? Default is true
 * @return {boolean}
 */
function isInViewport(element, skipBottom = true) {
  const elementRect = element.getBoundingClientRect();
  const outcome = (elementRect.left >= 0) &&
                  (elementRect.right <= (window.innerWidth || document.documentElement.clientWidth)) &&
                  (elementRect.top >= 0);
  if (skipBottom) {
    return outcome;
  }
  return (
    outcome &&
    (parseInt(element.style.bottom) || elementRect.bottom) <= (window.innerHeight || document.documentElement.clientHeight)
  );
}

/**
 * Populates if a DOM element is overlapped by other element
 * @param {object} DOM element
 * @return {boolean}
 */
function isOverlapped(element) {
  const elementRect = element.getBoundingClientRect();
  const topElement = document.elementFromPoint(elementRect.left, elementRect.top);
  if (!topElement) {
    return false;
  }
  else if ((element !== topElement && element.contains(topElement))) {
    return false;
  }
  return !element.isSameNode(topElement);
}

/**
 * Populates if target element is introducing a scroll in the Viewport
 * @param {object} Host DOM element
 * @param {object} Target DOM element
 * @return {boolean}
 */
function isTargetIntroducingScroll(host, target) {
  let hostElement = host;
  let menuElement = target;
  const hostElementRect = hostElement.getBoundingClientRect();

  // Test condition: If target is hidden and hostElement coordinates don't change, then no scroll is getting introduced 
  menuElement.style.display = 'none';
  const hostElementRectAfter = hostElement.getBoundingClientRect();
  
  if (hostElementRect.left === hostElementRectAfter.left && hostElementRect.right === hostElementRectAfter.right) {
    menuElement.style.display = '';
    return false;
  }
  else {
    menuElement.style.display = '';
    return true;
  }
}